import React, { useId, useState, useRef, useEffect } from 'react';
import Icon from './Icon';
import './Dropdown.css';

const Dropdown = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  searchable = false,
  searchFunction = null, // Custom search function: (option, searchTerm) => boolean
  searchPlaceholder = 'Search...',
  clearable = false,
  className = '',
  children,
  trigger,
  items = [],
  position = 'auto', // 'auto', 'left', 'right', 'center'
  isTableDropdown = false, // Special handling for table dropdowns
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [menuPosition, setMenuPosition] = useState({ left: 'auto', right: 'auto' });
  const [zIndex, setZIndex] = useState(10000); // Dynamic z-index
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  // Convert items to options format if items prop is used
  const dropdownOptions = items.length > 0
    ? items
      .filter(item => item.show !== false) // Filter out items where show is false
      .map((item, index) => ({
        value: item.value || index,
        label: item.label,
        onClick: item.onClick,
        disabled: item.disabled,
        className: item.className,
        icon: item.icon,
        badge: item.badge
      }))
    : options;

  const filteredOptions = searchable
    ? dropdownOptions.filter(option => {
      if (searchFunction && typeof searchFunction === 'function') {
        return searchFunction(option, searchTerm);
      }
      return option.label.toLowerCase().includes(searchTerm.toLowerCase());
    })
    : dropdownOptions;

  const selectedOption = dropdownOptions.find(option => option.value === value);

  // Dynamic z-index management - ensure currently open dropdown is on top
  useEffect(() => {
    if (isOpen) {
      // Set a high z-index for the currently open dropdown
      const currentZIndex = 10000 + Date.now() % 1000; // Unique high z-index
      setZIndex(currentZIndex);

      // Apply the z-index to the dropdown menu and set CSS variable
      if (dropdownRef.current) {
        const menu = dropdownRef.current.querySelector('.dropdown-menu');
        if (menu) {
          menu.style.zIndex = currentZIndex;
          menu.style.setProperty('--dropdown-z-index', currentZIndex);
        }

        // Find parent card and add dropdown-active class to ensure proper stacking
        const parentCard = dropdownRef.current.closest('.card, .tenant-card, .po-card, .inventory-card, .supplier-card, .machine-card');
        if (parentCard) {
          parentCard.classList.add('dropdown-active');
        }
      }
    } else {
      // Reset to default z-index when closed
      setZIndex(10000);

      // Remove dropdown-active class from parent card
      if (dropdownRef.current) {
        const parentCard = dropdownRef.current.closest('.card, .tenant-card, .po-card, .inventory-card, .supplier-card, .machine-card');
        if (parentCard) {
          parentCard.classList.remove('dropdown-active');
        }
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchable && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen, searchable]);

  // Enhanced smart positioning logic with improved edge detection
  useEffect(() => {
    if (!isOpen || !dropdownRef.current) {
      return;
    }

    const dropdownNode = dropdownRef.current;
    const calculatePosition = () => {
      if (!dropdownNode) {
        return;
      }
      const rect = dropdownNode.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const SIDE_BUFFER = 20;
      const VERTICAL_THRESHOLD = 150;
      const EDGE_MARGIN = 8;

      const menuWidth = Math.max(200, rect.width);
      const estimatedItemHeight = 44;
      const searchHeight = searchable ? 60 : 0;
      const maxVisibleItems = 6;
      const estimatedMenuHeight = Math.min(
        300,
        Math.min(filteredOptions.length, maxVisibleItems) * estimatedItemHeight + searchHeight + 20
      );

      let newPosition = { left: 'auto', right: 'auto' };


      if (isTableDropdown) {
        // Find the dropdown menu element (not the container)
        const menuElement = dropdownNode.querySelector('.dropdown-menu');
        if (!menuElement) {
          return;
        }

        // Force width to max-content to get the true content width
        // This overrides the CSS 'width: 100%' which causes issues
        menuElement.style.width = 'max-content';
        // Also override min-width otherwise min-width: 100% will make it full viewport width when fixed
        menuElement.style.minWidth = '200px';

        // Get the actual menu width for accurate overflow detection
        const menuRect = menuElement.getBoundingClientRect();
        const actualMenuWidth = menuRect.width;

        menuElement.style.setProperty('--dropdown-position', 'fixed');
        menuElement.classList.add('table-dropdown');

        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        let topPosition;
        let leftPosition;

        if (spaceBelow < VERTICAL_THRESHOLD && spaceAbove > estimatedMenuHeight) {
          topPosition = rect.top - estimatedMenuHeight - 4;
          menuElement.style.setProperty('--dropdown-top', `${topPosition}px`);
          menuElement.style.setProperty('--dropdown-bottom', 'auto');
        } else {
          topPosition = rect.bottom + 4;
          menuElement.style.setProperty('--dropdown-top', `${topPosition}px`);
          menuElement.style.setProperty('--dropdown-bottom', 'auto');
        }

        // Use actual menu width for overflow detection
        if (rect.left + actualMenuWidth > viewportWidth - SIDE_BUFFER) {
          // Align dropdown's right edge to trigger's right edge when near viewport edge
          const rightPosition = viewportWidth - rect.right;
          menuElement.style.setProperty('--dropdown-right', `${rightPosition}px`);
          menuElement.style.setProperty('--dropdown-left', 'auto');
        } else {
          // Default: align dropdown's left edge to trigger's left edge
          leftPosition = rect.left;
          menuElement.style.setProperty('--dropdown-left', `${leftPosition}px`);
          menuElement.style.setProperty('--dropdown-right', 'auto');
        }

        const maxHeight = Math.min(
          estimatedMenuHeight,
          Math.max(200, Math.max(spaceBelow, spaceAbove) - EDGE_MARGIN)
        );
        menuElement.style.setProperty('--dropdown-max-height', `${maxHeight}px`);
        menuElement.style.setProperty(
          '--dropdown-options-max-height',
          `${maxHeight - searchHeight - 20}px`
        );

        // Don't set menuPosition for table dropdowns - we use CSS custom properties instead
        // Setting menuPosition would create inline styles that override the CSS variables
        return;
      }

      if (position === 'auto') {
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        if (rect.left + menuWidth > viewportWidth - SIDE_BUFFER) {
          newPosition.right = '0';
          newPosition.left = 'auto';
        } else if (rect.right - menuWidth < SIDE_BUFFER) {
          newPosition.left = '0';
          newPosition.right = 'auto';
        } else {
          newPosition.left = '0';
          newPosition.right = 'auto';
        }

        let availableHeight = 0;
        let positionAbove = false;

        if (spaceBelow < VERTICAL_THRESHOLD && spaceAbove > estimatedMenuHeight) {
          positionAbove = true;
          availableHeight = spaceAbove - EDGE_MARGIN;
        } else if (spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow) {
          positionAbove = true;
          availableHeight = spaceAbove - EDGE_MARGIN;
        } else {
          availableHeight = spaceBelow - EDGE_MARGIN;
        }

        if (positionAbove) {
          dropdownNode.style.setProperty('--dropdown-top', 'auto');
          dropdownNode.style.setProperty('--dropdown-bottom', '100%');
          dropdownNode.style.setProperty('--dropdown-margin-top', '0');
          dropdownNode.style.setProperty('--dropdown-margin-bottom', '4px');
        } else {
          dropdownNode.style.setProperty('--dropdown-top', '100%');
          dropdownNode.style.setProperty('--dropdown-bottom', 'auto');
          dropdownNode.style.setProperty('--dropdown-margin-top', '4px');
          dropdownNode.style.setProperty('--dropdown-margin-bottom', '0');
        }

        const maxHeight = Math.min(estimatedMenuHeight, Math.max(200, availableHeight));
        dropdownNode.style.setProperty('--dropdown-max-height', `${maxHeight}px`);
        dropdownNode.style.setProperty(
          '--dropdown-options-max-height',
          `${maxHeight - searchHeight - 20}px`
        );
      } else if (position === 'right') {
        newPosition.right = '0';
        newPosition.left = 'auto';
      } else if (position === 'left') {
        newPosition.left = '0';
        newPosition.right = 'auto';
      } else if (position === 'center') {
        newPosition.left = '50%';
        newPosition.right = 'auto';
        dropdownNode.style.setProperty('--dropdown-transform', 'translateX(-50%)');
      }

      setMenuPosition(newPosition);
    };

    calculatePosition();

    const handleResize = () => calculatePosition();
    const handleScroll = () => calculatePosition();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);

      if (dropdownNode) {
        dropdownNode.style.removeProperty('--dropdown-top');
        dropdownNode.style.removeProperty('--dropdown-bottom');
        dropdownNode.style.removeProperty('--dropdown-margin-top');
        dropdownNode.style.removeProperty('--dropdown-margin-bottom');
        dropdownNode.style.removeProperty('--dropdown-transform');
        dropdownNode.style.removeProperty('--dropdown-max-height');
        dropdownNode.style.removeProperty('--dropdown-options-max-height');
      }
    };
  }, [isOpen, position, filteredOptions.length, searchable, isTableDropdown]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
      setHighlightedIndex(-1);
    }
  };

  const handleSelect = (option) => {
    if (option.onClick) {
      option.onClick();
    } else if (onChange) {
      onChange(option.value);
    }
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const dropdownId = useId();
  const listboxId = `${dropdownId}-listbox`;
  const triggerProps = {
    onClick: handleToggle,
    onKeyDown: handleKeyDown,
    tabIndex: disabled ? -1 : 0,
    role: 'combobox',
    'aria-expanded': isOpen,
    'aria-haspopup': 'listbox',
    'aria-label': placeholder,
    'aria-controls': listboxId,
    id: dropdownId
  };

  return (
    <div
      ref={dropdownRef}
      className={`dropdown ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''} ${isTableDropdown ? 'dropdown-table-mode' : ''} ${className}`}
      {...props}
    >
      {trigger ? (
        <div
          className="dropdown-trigger-custom"
          {...triggerProps}
        >
          {trigger}
        </div>
      ) : children ? (
        <div
          className="dropdown-trigger-custom"
          {...triggerProps}
        >
          {children}
        </div>
      ) : (
        <div
          className="dropdown-trigger"
          {...triggerProps}
        >
          <span className="dropdown-value">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <div className="dropdown-actions">
            {clearable && value && (
              <button
                className="dropdown-clear"
                onClick={handleClear}
                type="button"
                aria-label="Clear selection"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
            <svg
              className="dropdown-arrow"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      )}

      {isOpen && (
        <div
          className="dropdown-menu"
          role="listbox"
          id={listboxId}
          style={{
            // For table dropdowns, don't set left/right inline styles
            // They would override the CSS custom properties we set dynamically
            ...(isTableDropdown ? {} : {
              left: menuPosition.left,
              right: menuPosition.right
            }),
            top: 'var(--dropdown-top)',
            bottom: 'var(--dropdown-bottom)',
            marginTop: 'var(--dropdown-margin-top)',
            marginBottom: 'var(--dropdown-margin-bottom)',
            transform: 'var(--dropdown-transform)',
            zIndex: zIndex // Apply dynamic z-index
          }}
        >
          {searchable && (
            <div className="dropdown-search">
              <input
                ref={searchRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder}
                className="dropdown-search-input"
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setHighlightedIndex(0);
                  }
                }}
              />
            </div>
          )}

          <div className="dropdown-options">
            {filteredOptions.length === 0 ? (
              <div className="dropdown-empty">
                {searchTerm ? 'No options found' : 'No options available'}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                // Handle divider option
                if (option.value === 'divider') {
                  return (
                    <div
                      key={`divider-${index}`}
                      className="dropdown-divider"
                      role="separator"
                    />
                  );
                }

                return (
                  <div
                    key={option.value}
                    className={`dropdown-option ${index === highlightedIndex ? 'highlighted' : ''} ${option.disabled ? 'disabled' : ''} ${option.className || ''}`}
                    onClick={() => !option.disabled && handleSelect(option)}
                    role="option"
                    aria-selected={option.value === value}
                  >
                    {option.icon && (
                      <span className="dropdown-option-icon">
                        <Icon name={option.icon} size="small" />
                      </span>
                    )}
                    <span className="dropdown-option-label">{option.label}</span>
                    {option.badge && (
                      <span className="dropdown-option-badge">{option.badge}</span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
